import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin-5390214552274/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // Lock after too many attempts
  useEffect(() => {
    if (attempts >= 5) {
      setLocked(true);
      const timer = setTimeout(() => {
        setLocked(false);
        setAttempts(0);
      }, 60000); // 1 minute lockout
      return () => clearTimeout(timer);
    }
  }, [attempts]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (locked) {
      toast.error("Too many attempts. Please wait.");
      return;
    }

    if (!navigator.onLine) {
      toast.error("You are offline. Cannot login.");
      return;
    }

    setLoading(true);

    try {
      const result = await login(username, password);
      if (result.success) {
        toast.success("[ ACCESS GRANTED ]");
        navigate("/admin-5390214552274/dashboard");
      } else {
        setAttempts((prev) => prev + 1);
        toast.error(result.error || "[ ACCESS DENIED ]");
      }
    } catch (error) {
      setAttempts((prev) => prev + 1);
      toast.error("[ CONNECTION ERROR ]");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container grid-pattern" data-testid="admin-login">
      <div className="login-box card-glow animate-slide-up">
        {/* Header */}
        <div className="border-b border-[#15803d] pb-4 mb-6">
          <h1 className="text-2xl md:text-3xl text-[#22c55e] text-glow text-center font-['VT323']">
            [ SYSTEM ACCESS ]
          </h1>
          <p className="text-[#06b6d4] text-center text-sm mt-2">
            Authorization Required
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[#06b6d4] text-sm uppercase tracking-wider mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-transparent border border-[#15803d] text-[#22c55e] px-4 py-3 focus:border-[#22c55e] focus:outline-none font-mono input-terminal"
              placeholder="Enter username..."
              autoComplete="username"
              disabled={loading || locked}
              data-testid="username-input"
            />
          </div>

          <div>
            <label className="block text-[#06b6d4] text-sm uppercase tracking-wider mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border border-[#15803d] text-[#22c55e] px-4 py-3 focus:border-[#22c55e] focus:outline-none font-mono input-terminal"
              placeholder="Enter password..."
              autoComplete="current-password"
              disabled={loading || locked}
              data-testid="password-input"
            />
          </div>

          {locked && (
            <div className="text-[#ef4444] text-sm text-center animate-fade-in">
              [ LOCKED ] Too many attempts. Wait 60 seconds.
            </div>
          )}

          <button
            type="submit"
            disabled={loading || locked || !username || !password}
            className="w-full border border-[#22c55e] text-[#22c55e] py-3 uppercase tracking-widest font-mono btn-terminal disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-btn"
          >
            {loading ? "[ AUTHENTICATING... ]" : "[ LOGIN ]"}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-[#15803d] text-center">
          <p className="text-[#4ade80] text-xs opacity-60">
            Authorized personnel only
          </p>
        </div>
      </div>
    </div>
  );
}
